<template>
  <HrPageLayout
    title="Leave Balances Management"
    subtitle="Manage employee leave balances and allocations"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Leave & Attendance' },
      { label: 'Leave Balances' }
    ]"
  >
    <template #header-actions>
      <select class="hr-select" style="width: 120px;" v-model="selectedYear" @change="onFiltersChange">
        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
      <select class="hr-select" style="width: 180px;" v-model="selectedDepartment" @change="onFiltersChange">
        <option value="">All Departments</option>
        <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
      </select>
      <select class="hr-select" style="width: 180px;" v-model="initStatusFilter" @change="onFiltersChange">
        <option value="">All Status</option>
        <option value="not_initialized">Not Initialized</option>
        <option value="partial">Partially Initialized</option>
        <option value="initialized">Fully Initialized</option>
      </select>
      <button class="hr-btn hr-btn-secondary" @click="selectAllEmployees" :disabled="selectingAll" style="position:relative;">
        <i class="bx" :class="selectingAll ? 'bx-loader-alt bx-spin' : 'bx-select-multiple'"></i>
        {{ selectingAll ? 'Loading...' : 'Select All Employees' }}
      </button>
      <button class="hr-btn hr-btn-primary" @click="showBulkInitializeModal" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-plus-circle"></i> Bulk Initialize ({{ selectedEmployees.length }})
      </button>
      <button class="hr-btn hr-btn-danger" @click="showBulkUninitializeModal" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-reset"></i> Bulk Uninitialize ({{ selectedEmployees.length }})
      </button>
      <button class="hr-btn hr-btn-info" @click="showCarryForwardModal" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-transfer"></i> Carry Forward
      </button>
      <button class="hr-btn hr-btn-warning" @click="showCashOutModal" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-money"></i> Cash Out
      </button>
      <button class="hr-btn hr-btn-success" @click="downloadReport" :disabled="downloadLoading">
        <i class="bx bx-download"></i> {{ downloadLoading ? 'Downloading...' : 'Export' }}
      </button>
    </template>

    <div class="leave-balances-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card" :class="'gradient-' + (statsCards.indexOf(stat) + 1)" v-for="stat in statsCards" :key="stat.key">
          <div class="hr-metric-icon"><i :class="'bx ' + stat.icon"></i></div>
          <p class="hr-metric-label">{{ stat.label }}</p>
          <h2 class="hr-metric-value">{{ metrics[stat.key] || 0 }}</h2>
        </div>
      </div>

      <!-- Main Table Card -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Employee Leave Balances</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input v-model="searchValue" type="text" class="hr-input" placeholder="Search by name, emp no, or ID..." @keyup.enter="fetchEmployees">
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading employee leave balances...</p>
          </div>

          <div v-else-if="employees.length === 0" class="hr-empty-state">
            <i class="bx bx-user-x"></i>
            <h5>No employees found</h5>
            <p>Try adjusting your filters</p>
          </div>

          <template v-else>
            <div v-if="selectedEmployees.length > 0" class="selection-bar hr-mb-3">
              <span>
                <i class="bx bx-check-square"></i>
                <strong>{{ selectedEmployees.length }}</strong> employee(s) selected
              </span>
              <button class="hr-btn hr-btn-secondary hr-btn-sm" @click="clearSelection">Clear</button>
            </div>

            <div v-if="showSelectAllBanner" class="alert alert-info hr-mb-3 select-all-banner">
              <span>
                <i class="bx bx-info-circle me-1"></i>
                All <strong>{{ employees.length }}</strong> on this page are selected.
                Select all <strong>{{ totalItems }}</strong> employees matching filters?
              </span>
              <div class="select-all-actions">
                <button class="hr-btn hr-btn-primary hr-btn-sm" @click="selectAllEmployees" :disabled="selectingAll">
                  {{ selectingAll ? 'Loading...' : `Select All ${totalItems}` }}
                </button>
                <button class="hr-btn hr-btn-secondary hr-btn-sm" @click="clearSelection">Clear</button>
              </div>
            </div>

            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th style="width: 40px;">
                      <input type="checkbox" class="hr-checkbox" :checked="isPageSelected" @change="toggleSelectPage" :title="isPageSelected ? 'Deselect page' : 'Select this page'">
                    </th>
                    <th>Employee</th>
                    <th>Department</th>
                    <th
                      v-for="lt in leaveTypes"
                      :key="'hdr-' + lt.id"
                      class="leave-type-col"
                      :title="lt.typeName"
                    >
                      <div v-if="lt.headerAcronym" class="leave-type-hdr">
                        <span class="leave-type-acronym">{{ lt.headerAcronym }}</span>
                        <small class="leave-type-desc">{{ lt.headerLabel }}</small>
                      </div>
                      <span v-else>{{ shortLeaveTypeName(lt.typeName) }}</span>
                    </th>
                    <th>Status</th>
                    <th style="text-align: center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="emp in employees" :key="emp.nationalId">
                    <td>
                      <input
                        type="checkbox"
                        class="hr-checkbox"
                        :checked="isEmployeeSelected(emp.nationalId)"
                        @change="toggleEmployeeSelection(emp.nationalId, $event)"
                      >
                    </td>
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2">
                        <div class="avatar-initials">{{ getInitials(emp.fullName) }}</div>
                        <div>
                          <div class="emp-name">{{ emp.fullName }}</div>
                          <small class="emp-meta">{{ emp.empNo }} | {{ emp.nationalId }}</small>
                        </div>
                      </div>
                    </td>
                    <td><span class="hr-badge hr-badge-muted">{{ emp.departmentName }}</span></td>
                    <td
                      v-for="lt in leaveTypes"
                      :key="emp.nationalId + '-' + lt.id"
                      class="leave-type-col"
                      style="text-align: center;"
                    >
                      <span v-if="!leaveTypeApplies(emp, lt)" class="leave-balance-na" title="Not applicable">—</span>
                      <span
                        v-else-if="getBalanceCell(emp, lt.id).initialized"
                        class="leave-balance-val"
                        :title="balanceTooltip(getBalanceCell(emp, lt.id))"
                      >
                        {{ getBalanceCell(emp, lt.id).remainingDays }}
                        <small class="leave-balance-sub">/ {{ balanceTotalDisplay(getBalanceCell(emp, lt.id)) }}</small>
                      </span>
                      <span v-else class="leave-balance-missing" title="Not initialized">—</span>
                    </td>
                    <td><span class="hr-badge" :class="getStatusClass(emp.initializationStatus)">{{ emp.initializationStatus }}</span></td>
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                        <button @click="viewEmployeeDetails(emp)" class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" title="View & Manage Balances"><i class="bx bx-show"></i></button>
                        <button v-if="emp.initializationStatus !== 'Initialized'" @click="initializeSingle(emp)" class="hr-btn hr-btn-success hr-btn-sm hr-btn-icon" :disabled="emp.processing" title="Initialize Balances"><i class="bx bx-check-square"></i></button>
                        <button @click="openAdjustModal(emp)" class="hr-btn hr-btn-warning hr-btn-sm hr-btn-icon" title="Quick Adjust"><i class="bx bx-edit"></i></button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="totalPages > 1" class="hr-pagination">
              <div class="hr-pagination-info">Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries</div>
              <nav class="hr-pagination-nav">
                <button class="hr-page-btn" :disabled="currentPage === 1" @click="prevPage">Previous</button>
                <template v-for="page in visiblePages" :key="page">
                  <button v-if="page !== '...'" class="hr-page-btn" :class="{ active: page === currentPage }" @click="goToPage(page)">{{ page }}</button>
                  <span v-else class="hr-page-btn ellipsis">...</span>
                </template>
                <button class="hr-page-btn" :disabled="currentPage === totalPages" @click="nextPage">Next</button>
              </nav>
            </div>
          </template>
        </div>
      </div>

    <!-- Teleport all modals to body to escape the z-index stacking context of dashboard-content -->
    <Teleport to="body">

    <!-- Employee Leave Details Modal -->
    <div class="modal fade" id="employee-details-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-account-details me-2"></i>
              Leave Balances - {{ selectedEmployee.fullName || selectedEmployee.firstName + ' ' + selectedEmployee.lastName }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Employee Info Header -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label class="text-muted small">Employee No</label>
                <p class="fw-medium mb-0">{{ selectedEmployee.empNo }}</p>
              </div>
              <div class="col-md-3">
                <label class="text-muted small">National ID</label>
                <p class="fw-medium mb-0">{{ selectedEmployee.nationalId }}</p>
              </div>
              <div class="col-md-3">
                <label class="text-muted small">Department</label>
                <p class="fw-medium mb-0">{{ selectedEmployee.departmentName }}</p>
              </div>
              <div class="col-md-3">
                <label class="text-muted small">Hire Date</label>
                <p class="fw-medium mb-0">{{ formatDate(selectedEmployee.hireDate) }}</p>
              </div>
            </div>

            <hr>

            <!-- Leave Balances Table -->
            <h6 class="text-primary mb-3">
              <i class="mdi mdi-calendar-check me-1"></i>
              Leave Balances for {{ selectedYear }}
            </h6>

            <div v-if="detailsLoading" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2">Loading balances...</span>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-bordered table-striped">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th class="text-center">Allocated</th>
                  <th class="text-center">Carried Over</th>
                  <th class="text-center">Total Available</th>
                  <th class="text-center">Used</th>
                  <th class="text-center">Remaining</th>
                  <th class="text-center">Status</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="balance in employeeBalances" :key="balance.leaveTypeId" :class="{ 'table-secondary': balance.appliesToEmployee === false }">
                  <td>
                    <strong>{{ balance.typeName }}</strong>
                    <br>
                    <small class="text-muted">Default: {{ balance.defaultAllocation }} days</small>
                  </td>
                  <td v-if="balance.appliesToEmployee === false" colspan="6" class="text-center text-muted">Not applicable for this employee</td>
                  <template v-else>
                  <td class="text-center">{{ balance.totalDays || 0 }}</td>
                  <td class="text-center">{{ balance.carriedOverDays || 0 }}</td>
                  <td class="text-center fw-medium">
                    {{ (parseFloat(balance.totalDays || 0) + parseFloat(balance.carriedOverDays || 0)).toFixed(1) }}
                  </td>
                  <td class="text-center">
                    <span :class="balance.usedDays > 0 ? 'text-warning' : ''">
                      {{ balance.usedDays || 0 }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="getRemainingClass(balance)">
                      {{ balance.remainingDays || 0 }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="balance.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                      {{ balance.status }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button
                        class="btn btn-sm btn-outline-primary"
                        @click="editBalance(balance)"
                        title="Edit Balance"
                    >
                      <i class="mdi mdi-pencil"></i>
                    </button>
                  </td>
                  </template>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Leave History Section -->
            <hr class="my-4">
            <h6 class="text-primary mb-3">
              <i class="mdi mdi-history me-1"></i>
              Recent Leave History
            </h6>

            <div v-if="leaveHistory.length === 0" class="text-center text-muted py-3">
              <i class="mdi mdi-information-outline"></i> No leave records for this year
            </div>

            <div v-else class="table-responsive">
              <table class="table table-sm">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th>Period</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Approved By</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="record in leaveHistory" :key="record.id">
                  <td>{{ record.leaveTypeName }}</td>
                  <td>{{ formatDate(record.startDate) }} - {{ formatDate(record.endDate) }}</td>
                  <td>{{ record.totalDays }}</td>
                  <td>
                    <span class="badge" :class="getLeaveStatusClass(record.status)">
                      {{ record.status }}
                    </span>
                  </td>
                  <td>{{ record.approvedBy || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button
                type="button"
                class="btn btn-danger"
                @click="openRollbackModal"
                :disabled="!hasCarriedOverDays"
            >
              <i class="mdi mdi-undo me-1"></i>
              Rollback Carry Forward
            </button>
            <button
                type="button"
                class="btn btn-warning"
                @click="openSingleCashOutModal"
            >
              <i class="mdi mdi-cash me-1"></i>
              Cash Out Leave
            </button>
            <button
                type="button"
                class="btn btn-info"
                @click="openSingleCarryForward"
            >
              <i class="mdi mdi-transfer-right me-1"></i>
              Carry Forward
            </button>
            <button
                type="button"
                class="btn btn-outline-danger"
                @click="showSingleUninitializeModal"
                :disabled="uninitializingSingle"
            >
              <i class="mdi mdi-backup-restore me-1"></i>
              Reset Balances
            </button>
            <button
                type="button"
                class="btn btn-success"
                @click="initializeFromModal"
                :disabled="isFullyInitialized"
            >
              <i class="mdi mdi-playlist-plus me-1"></i>
              Initialize Missing
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Balance Modal -->
    <div class="modal fade" id="edit-balance-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-warning">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-pencil me-2"></i>
              Update Leave Balance
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-medium">Employee</label>
              <p class="mb-0">{{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label fw-medium">Leave Type</label>
              <p class="mb-0">{{ editForm.leaveTypeName }}</p>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Allocated Days <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    v-model="editForm.totalDays"
                    min="0"
                    step="0.5"
                    required
                >
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Carried Over Days</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="editForm.carriedOverDays"
                    min="0"
                    step="0.5"
                >
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Used Days <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    v-model="editForm.usedDays"
                    min="0"
                    step="0.5"
                    required
                >
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Remaining Days</label>
                <input
                    type="number"
                    class="form-control"
                    :value="calculateRemaining"
                    disabled
                >
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Remarks</label>
              <textarea
                  class="form-control"
                  v-model="editForm.remarks"
                  rows="2"
                  placeholder="Reason for adjustment..."
              ></textarea>
            </div>

            <div class="alert alert-info small mb-0">
              <i class="mdi mdi-information me-1"></i>
              Use this to manually update leave balances for employees who took leave without applying through the system.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                @click="saveBalance"
                :disabled="saving"
            >
              <span v-if="saving">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Saving...
              </span>
              <span v-else>
                <i class="mdi mdi-content-save me-1"></i>
                Save Changes
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Adjust Modal -->
    <div class="modal fade" id="quick-adjust-modal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-tune me-2"></i>
              Quick Balance Adjustment
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-medium">Employee</label>
              <p class="mb-0">{{ adjustForm.employeeName }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Leave Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="adjustForm.leaveTypeId" required>
                <option value="">Select Leave Type</option>
                <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">
                  {{ lt.typeName }}
                </option>
              </select>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Adjustment Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="adjustForm.adjustmentType">
                  <option value="deduct">Deduct Days (Leave Taken)</option>
                  <option value="add">Add Days (Credit Back)</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Days <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    v-model="adjustForm.days"
                    min="0.5"
                    step="0.5"
                    required
                >
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Reason <span class="text-danger">*</span></label>
              <textarea
                  class="form-control"
                  v-model="adjustForm.reason"
                  rows="2"
                  placeholder="e.g., Leave taken 10-15 Nov without system application"
                  required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                @click="submitAdjustment"
                :disabled="adjusting"
            >
              <span v-if="adjusting">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi mdi-check me-1"></i>
                Apply Adjustment
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Uninitialize Confirmation Modal -->
    <div class="modal fade" id="bulk-uninit-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-backup-restore me-2"></i>
              Bulk Uninitialize Leave Balances
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>You are about to remove leave balances for <strong>{{ selectedEmployees.length }}</strong> employee(s) for <strong>{{ selectedYear }}</strong>.</p>
            <div class="alert alert-warning">
              <i class="mdi mdi-alert me-1"></i>
              This deletes balance records so you can initialize afresh. It does not delete leave requests.
            </div>
            <div class="form-check mb-0">
              <input class="form-check-input" type="checkbox" id="bulk-uninit-force" v-model="uninitializeForce">
              <label class="form-check-label" for="bulk-uninit-force">
                Force reset employees who already have used leave days recorded
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="executeBulkUninitialize" :disabled="bulkUninitializing">
              <span v-if="bulkUninitializing">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Removing...
              </span>
              <span v-else>
                <i class="mdi mdi-delete-sweep me-1"></i>
                Yes, Uninitialize
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Single Uninitialize Confirmation Modal -->
    <div class="modal fade" id="single-uninit-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-backup-restore me-2"></i>
              Reset Leave Balances
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>
              Remove all leave balance records for
              <strong>{{ selectedEmployee.fullName || selectedEmployee.firstName }}</strong>
              ({{ selectedYear }})?
            </p>
            <div class="form-check mb-0">
              <input class="form-check-input" type="checkbox" id="single-uninit-force" v-model="uninitializeForce">
              <label class="form-check-label" for="single-uninit-force">
                Force reset even if leave days have been used
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="executeSingleUninitialize" :disabled="uninitializingSingle">
              <span v-if="uninitializingSingle">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Removing...
              </span>
              <span v-else>Yes, Reset</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Initialize Confirmation Modal -->
    <div class="modal fade" id="bulk-init-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-playlist-plus me-2"></i>
              Bulk Initialize Leave Balances
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>You are about to initialize leave balances for <strong>{{ selectedEmployees.length }}</strong> employee(s) for the year <strong>{{ selectedYear }}</strong>.</p>

            <div class="alert alert-warning">
              <i class="mdi mdi-alert me-1"></i>
              This creates balances for all active leave types that apply to each employee.
              ML (Maternity Leave) is only for Female staff; PL (Paternity Leave) only for Male staff.
              Existing balances are not overwritten.
            </div>

            <p class="mb-0">Do you want to proceed?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-success"
                @click="executeBulkInitialize"
                :disabled="bulkInitializing"
            >
              <span v-if="bulkInitializing">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Initializing...
              </span>
              <span v-else>
                <i class="mdi mdi-check-all me-1"></i>
                Yes, Initialize
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Carry Forward Modal -->
    <div class="modal fade" id="carry-forward-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-transfer-right me-2"></i>
              Carry Forward Leave Balances
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label">From Year <span class="text-danger">*</span></label>
                <select class="form-select" v-model="carryForwardForm.fromYear" @change="loadCarryForwardPreview">
                  <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">To Year <span class="text-danger">*</span></label>
                <select class="form-select" v-model="carryForwardForm.toYear">
                  <option v-for="year in toYearOptions" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Max Days to Carry</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="carryForwardForm.maxDays"
                    min="1"
                    max="30"
                    @change="loadCarryForwardPreview"
                >
              </div>
            </div>

            <div class="alert alert-info">
              <i class="mdi mdi-information me-1"></i>
              <strong>{{ selectedEmployees.length }}</strong> employee(s) selected.
              Remaining days from <strong>{{ carryForwardForm.fromYear }}</strong> will be carried forward to <strong>{{ carryForwardForm.toYear }}</strong> (max {{ carryForwardForm.maxDays }} days per leave type).
            </div>

            <!-- Preview for single employee -->
            <div v-if="selectedEmployees.length === 1 && carryForwardPreview.length > 0">
              <h6 class="text-primary mb-2">
                <i class="mdi mdi-eye me-1"></i>
                Preview
              </h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th class="text-center">Remaining ({{ carryForwardForm.fromYear }})</th>
                    <th class="text-center">Eligible to Carry</th>
                    <th class="text-center">Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in carryForwardPreview" :key="item.leaveTypeID">
                    <td>{{ item.leaveTypeName }}</td>
                    <td class="text-center">{{ item.remainingDays }}</td>
                    <td class="text-center">
                      <span class="badge bg-success">{{ item.eligibleCarryForward }} days</span>
                    </td>
                    <td class="text-center">
                        <span class="badge" :class="item.action === 'Will Create' ? 'bg-primary' : 'bg-warning'">
                          {{ item.action }}
                        </span>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <th>Total</th>
                    <th class="text-center">-</th>
                    <th class="text-center">{{ totalEligibleDays }} days</th>
                    <th></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div v-if="previewLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2">Loading preview...</span>
            </div>

            <div class="alert alert-warning mt-3">
              <i class="mdi mdi-alert me-1"></i>
              <strong>Note:</strong> If target year balances already exist, the carried forward days will be added to the existing carried over amount.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-info"
                @click="executeCarryForward"
                :disabled="carryingForward || carryForwardForm.fromYear >= carryForwardForm.toYear"
            >
              <span v-if="carryingForward">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi mdi-transfer-right me-1"></i>
                Carry Forward
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Single Employee Carry Forward Modal -->
    <div class="modal fade" id="single-carry-forward-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-transfer-right me-2"></i>
              Carry Forward - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label">From Year <span class="text-danger">*</span></label>
                <select class="form-select" v-model="singleCarryForm.fromYear" @change="loadSingleCarryPreview">
                  <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">To Year <span class="text-danger">*</span></label>
                <select class="form-select" v-model="singleCarryForm.toYear">
                  <option v-for="year in singleToYearOptions" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Max Days to Carry</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="singleCarryForm.maxDays"
                    min="1"
                    max="30"
                    @change="loadSingleCarryPreview"
                >
              </div>
            </div>

            <!-- Preview Table -->
            <div v-if="singleCarryPreview.length > 0">
              <h6 class="text-primary mb-2 text-white">
                <i class="mdi mdi-eye me-1"></i>
                Carry Forward Preview
              </h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th class="text-center">Total ({{ singleCarryForm.fromYear }})</th>
                    <th class="text-center">Used</th>
                    <th class="text-center">Remaining</th>
                    <th class="text-center">Eligible to Carry</th>
                    <th class="text-center">Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in singleCarryPreview" :key="item.leaveTypeID">
                    <td>{{ item.leaveTypeName }}</td>
                    <td class="text-center">{{ item.totalDays }}</td>
                    <td class="text-center">{{ item.usedDays }}</td>
                    <td class="text-center">{{ item.remainingDays }}</td>
                    <td class="text-center">
                        <span class="badge" :class="item.eligibleCarryForward > 0 ? 'bg-success' : 'bg-secondary'">
                          {{ item.eligibleCarryForward }} days
                        </span>
                    </td>
                    <td class="text-center">
                        <span class="badge" :class="item.action === 'Will Create' ? 'bg-primary' : 'bg-warning'">
                          {{ item.action }}
                        </span>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <th colspan="4">Total Eligible to Carry Forward</th>
                    <th class="text-center">{{ singleTotalEligible }} days</th>
                    <th></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div v-else-if="!singlePreviewLoading" class="alert alert-warning">
              <i class="mdi mdi-alert me-1"></i>
              No leave balances found for {{ singleCarryForm.fromYear }}. Please select a different year.
            </div>

            <div v-if="singlePreviewLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2">Loading preview...</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-info"
                @click="executeSingleCarryForward"
                :disabled="singleCarryingForward || singleCarryForm.fromYear >= singleCarryForm.toYear || singleTotalEligible === 0"
            >
              <span v-if="singleCarryingForward">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi mdi-transfer-right me-1"></i>
                Carry Forward
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rollback Modal -->
    <div class="modal fade" id="rollback-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-danger">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-undo me-2"></i>
              Rollback Carry Forward - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <i class="mdi mdi-alert me-1"></i>
              <strong>Warning:</strong> This will remove the carried forward days from the selected year.
              If the employee has already used some of the carried forward days, the rollback may be partial.
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Year to Rollback <span class="text-danger">*</span></label>
                <select class="form-select" v-model="rollbackForm.year" @change="loadRollbackPreview">
                  <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Reason <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="rollbackForm.reason" placeholder="Reason for rollback">
              </div>
            </div>

            <!-- Rollback Preview -->
            <div v-if="rollbackPreview.length > 0">
              <h6 class="text-danger mb-2">
                <i class="mdi mdi-eye me-1"></i>
                Rollback Preview
              </h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th class="text-center">Carried Over Days</th>
                    <th class="text-center">Current Remaining</th>
                    <th class="text-center">After Rollback</th>
                    <th class="text-center">Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in rollbackPreview" :key="item.id">
                    <td>{{ item.leaveTypeName }}</td>
                    <td class="text-center">
                      <span class="badge bg-info">{{ item.carriedOverDays }} days</span>
                    </td>
                    <td class="text-center">{{ item.remainingDays }}</td>
                    <td class="text-center">{{ item.newRemainingAfterRollback }}</td>
                    <td class="text-center">
                        <span class="badge" :class="item.rollbackStatus.includes('Partial') ? 'bg-warning' : 'bg-success'">
                          {{ item.rollbackStatus }}
                        </span>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <th>Total to Remove</th>
                    <th class="text-center">{{ totalCarriedOverToRollback }} days</th>
                    <th colspan="3"></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div v-else-if="!rollbackPreviewLoading" class="alert alert-info">
              <i class="mdi mdi-information me-1"></i>
              No carried forward balances found for {{ rollbackForm.year }}.
            </div>

            <div v-if="rollbackPreviewLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2">Loading preview...</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-danger"
                @click="executeRollback"
                :disabled="rollingBack || rollbackPreview.length === 0 || !rollbackForm.reason"
            >
              <span v-if="rollingBack">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Rolling Back...
              </span>
              <span v-else>
                <i class="mdi mdi-undo me-1"></i>
                Confirm Rollback
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Single Employee Cash Out Modal -->
    <div class="modal fade" id="single-cashout-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-warning">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-cash me-2"></i>
              Cash Out Leave - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <i class="mdi mdi-information me-1"></i>
              Cash out allows employees to receive payment for unused leave days instead of carrying them forward.
            </div>

            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label">Year <span class="text-danger">*</span></label>
                <select class="form-select" v-model="cashOutForm.year" @change="loadCashOutPreview">
                  <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Leave Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="cashOutForm.leaveTypeId" @change="onLeaveTypeChange">
                  <option value="">Select Leave Type</option>
                  <option v-for="bal in cashOutBalances" :key="bal.leaveTypeID" :value="bal.leaveTypeID">
                    {{ bal.leaveTypeName }} ({{ bal.remainingDays }} days available)
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Days to Cash Out <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    v-model="cashOutForm.days"
                    min="0.5"
                    step="0.5"
                    :max="cashOutForm.maxDays"
                >
                <small class="text-muted" v-if="cashOutForm.maxDays">Max: {{ cashOutForm.maxDays }} days</small>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label">Amount Per Day (KES)</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="cashOutForm.amountPerDay"
                    min="0"
                    step="0.01"
                >
                <small class="text-muted" v-if="suggestedDailyRate">Suggested: {{ formatCurrency(suggestedDailyRate) }}</small>
              </div>
              <div class="col-md-4">
                <label class="form-label">Total Amount</label>
                <input
                    type="text"
                    class="form-control bg-light"
                    :value="formatCurrency(calculatedTotalAmount)"
                    disabled
                >
              </div>
              <div class="col-md-4">
                <label class="form-label">Payment Reference</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="cashOutForm.paymentReference"
                    placeholder="e.g., PAY-2024-001"
                >
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Remarks</label>
              <textarea
                  class="form-control"
                  v-model="cashOutForm.remarks"
                  rows="2"
                  placeholder="Any additional notes..."
              ></textarea>
            </div>

            <!-- Available Balances Preview -->
            <div v-if="cashOutBalances.length > 0" class="mt-3">
              <h6 class="text-primary mb-2">Available Balances for {{ cashOutForm.year }}</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th class="text-center">Total</th>
                    <th class="text-center">Used</th>
                    <th class="text-center">Available</th>
                    <th class="text-center">Paid Leave?</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="bal in cashOutBalances" :key="bal.leaveTypeID"
                      :class="{'table-primary': bal.leaveTypeID === cashOutForm.leaveTypeId}">
                    <td>{{ bal.leaveTypeName }}</td>
                    <td class="text-center">{{ bal.totalDays }}</td>
                    <td class="text-center">{{ bal.usedDays }}</td>
                    <td class="text-center">
                      <span class="badge bg-success">{{ bal.remainingDays }} days</span>
                    </td>
                    <td class="text-center">
                        <span class="badge" :class="bal.isPaid === '1' ? 'bg-success' : 'bg-secondary'">
                          {{ bal.isPaid === '1' ? 'Yes' : 'No' }}
                        </span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="cashOutPreviewLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2">Loading...</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-warning"
                @click="executeCashOut"
                :disabled="cashingOut || !cashOutForm.leaveTypeId || !cashOutForm.days || cashOutForm.days <= 0"
            >
              <span v-if="cashingOut">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi mdi-cash me-1"></i>
                Process Cash Out
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Cash Out Modal -->
    <div class="modal fade" id="bulk-cashout-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-warning">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-cash-multiple me-2"></i>
              Bulk Cash Out Leave
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p><strong>{{ selectedEmployees.length }}</strong> employee(s) selected for cash out.</p>

            <div class="mb-3">
              <label class="form-label">Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="bulkCashOutForm.year">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Leave Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="bulkCashOutForm.leaveTypeId">
                <option value="">Select Leave Type</option>
                <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">
                  {{ lt.typeName }}
                </option>
              </select>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Max Days Per Employee</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="bulkCashOutForm.maxDays"
                    min="0.5"
                    step="0.5"
                    placeholder="Leave empty for all"
                >
              </div>
              <div class="col-md-6">
                <label class="form-label">Amount Per Day (KES)</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="bulkCashOutForm.amountPerDay"
                    min="0"
                    step="0.01"
                >
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Payment Reference</label>
              <input
                  type="text"
                  class="form-control"
                  v-model="bulkCashOutForm.paymentReference"
                  placeholder="e.g., BULK-PAY-2024-001"
              >
            </div>

            <div class="mb-3">
              <label class="form-label">Remarks</label>
              <textarea
                  class="form-control"
                  v-model="bulkCashOutForm.remarks"
                  rows="2"
              ></textarea>
            </div>

            <div class="alert alert-warning">
              <i class="mdi mdi-alert me-1"></i>
              This will cash out available days for all selected employees. Employees without balances will be skipped.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-warning"
                @click="executeBulkCashOut"
                :disabled="bulkCashingOut || !bulkCashOutForm.leaveTypeId"
            >
              <span v-if="bulkCashingOut">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi mdi-cash-multiple me-1"></i>
                Process Bulk Cash Out
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Download Progress Modal -->
    <div class="modal fade" id="download-progress-modal" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-download me-2"></i>
              Downloading Report
            </h5>
          </div>
          <div class="modal-body text-center py-4">
            <div class="mb-4">
              <div class="download-icon-wrapper mb-3">
                <i class="mdi mdi-file-download-outline text-primary" style="font-size: 48px;"></i>
              </div>
              <h5 class="mb-1">{{ downloadProgress.status }}</h5>
              <p class="text-muted mb-0">{{ downloadProgress.message }}</p>
            </div>

            <!-- Progress Bar -->
            <div class="progress mb-3" style="height: 20px;">
              <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  :class="downloadProgress.percent === 100 ? 'bg-success' : 'bg-primary'"
                  role="progressbar"
                  :style="{ width: downloadProgress.percent + '%' }"
                  :aria-valuenow="downloadProgress.percent"
                  aria-valuemin="0"
                  aria-valuemax="100">
                {{ downloadProgress.percent }}%
              </div>
            </div>

            <!-- Stats -->
            <div class="row text-center">
              <div class="col-4">
                <div class="border-end">
                  <h5 class="mb-1 text-primary">{{ downloadProgress.fetched }}</h5>
                  <small class="text-muted">Records Fetched</small>
                </div>
              </div>
              <div class="col-4">
                <div class="border-end">
                  <h5 class="mb-1 text-info">{{ downloadProgress.totalRecords }}</h5>
                  <small class="text-muted">Total Records</small>
                </div>
              </div>
              <div class="col-4">
                <div>
                  <h5 class="mb-1 text-warning">{{ downloadProgress.timeRemaining }}</h5>
                  <small class="text-muted">Time Remaining</small>
                </div>
              </div>
            </div>

            <!-- Cancel Button -->
            <div class="mt-4" v-if="downloadProgress.percent < 100">
              <button
                  type="button"
                  class="btn btn-outline-danger"
                  @click="cancelDownload"
              >
                <i class="mdi mdi-close me-1"></i>
                Cancel Download
              </button>
            </div>

            <!-- Done Button -->
            <div class="mt-4" v-if="downloadProgress.percent === 100">
              <button
                  type="button"
                  class="btn btn-success"
                  data-bs-dismiss="modal"
              >
                <i class="mdi mdi-check me-1"></i>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Modals -->
    <SuccessModal
        title="Operation Successful"
        heading="Success!"
        :message="successMessage"
    />
    <ErrorModal
        title="Operation Failed"
        heading="Error"
        :message="errorMessage"
    />

    </Teleport>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import SuccessModal from "./modals/SuccessModal.vue";
import ErrorModal from "./modals/ErrorModal.vue";
import { authUrl, execute, executeDownload } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "LeaveBalancesManagement",
  components: {
    HrPageLayout,
    SuccessModal,
    ErrorModal
  },
  data() {
    return {
      // Loading states
      loading: false,
      detailsLoading: false,
      downloadLoading: false,
      saving: false,
      adjusting: false,
      bulkInitializing: false,
      bulkUninitializing: false,
      uninitializingSingle: false,
      uninitializeForce: false,

      // Messages
      successMessage: '',
      errorMessage: '',

      // Filters
      searchValue: '',
      selectedYear: new Date().getFullYear(),
      selectedDepartment: '',
      initStatusFilter: '',

      // Data
      employees: [],
      departments: [],
      leaveTypes: [],
      availableYears: [],
      selectedEmployees: [],
      selectingAll: false,
      showSelectAllBanner: false,

      // Pagination
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      totalItems: 0,

      // Metrics
      metrics: {
        totalEmployees: 0,
        fullyInitialized: 0,
        notInitialized: 0,
        partiallyInitialized: 0
      },

      // Stats configuration
      statsCards: [
        { key: 'totalEmployees', label: 'Total Active Employees', icon: 'bx-group' },
        { key: 'fullyInitialized', label: 'Fully Initialized', icon: 'bx-check-circle' },
        { key: 'partiallyInitialized', label: 'Partially Initialized', icon: 'bx-error-circle' },
        { key: 'notInitialized', label: 'Not Initialized', icon: 'bx-x-circle' }
      ],

      // Selected employee details
      selectedEmployee: {},
      employeeBalances: [],
      leaveHistory: [],

      // Edit form
      editForm: {
        leaveTypeId: '',
        leaveTypeName: '',
        totalDays: 0,
        usedDays: 0,
        carriedOverDays: 0,
        remarks: ''
      },

      // Quick adjust form
      adjustForm: {
        nationalId: '',
        employeeName: '',
        leaveTypeId: '',
        adjustmentType: 'deduct',
        days: 1,
        reason: ''
      },

      // Carry forward form (bulk)
      carryForwardForm: {
        fromYear: new Date().getFullYear() - 1,
        toYear: new Date().getFullYear(),
        maxDays: 5
      },
      carryForwardPreview: [],
      totalEligibleDays: 0,
      previewLoading: false,
      carryingForward: false,

      // Single employee carry forward
      singleCarryForm: {
        fromYear: new Date().getFullYear() - 1,
        toYear: new Date().getFullYear(),
        maxDays: 5
      },
      singleCarryPreview: [],
      singleTotalEligible: 0,
      singlePreviewLoading: false,
      singleCarryingForward: false,

      // Rollback form
      rollbackForm: {
        year: new Date().getFullYear(),
        reason: ''
      },
      rollbackPreview: [],
      totalCarriedOverToRollback: 0,
      rollbackPreviewLoading: false,
      rollingBack: false,

      // Single cash out form
      cashOutForm: {
        year: new Date().getFullYear(),
        leaveTypeId: '',
        days: 0,
        maxDays: 0,
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      },
      cashOutBalances: [],
      suggestedDailyRate: 0,
      cashOutPreviewLoading: false,
      cashingOut: false,

      // Bulk cash out form
      bulkCashOutForm: {
        year: new Date().getFullYear(),
        leaveTypeId: '',
        maxDays: '',
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      },
      bulkCashingOut: false,

      // Download progress
      downloadProgress: {
        status: 'Preparing...',
        message: 'Initializing download...',
        percent: 0,
        fetched: 0,
        totalRecords: 0,
        timeRemaining: '--:--',
        cancelled: false,
        startTime: null
      },
      allDownloadData: []
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },
    isAllSelected() {
      return this.employees.length > 0 &&
          this.selectedEmployees.length === this.totalItems;
    },
    isPageSelected() {
      return this.employees.length > 0 &&
          this.employees.every(emp => this.isEmployeeSelected(emp.nationalId));
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }

      return pages;
    },
    calculateRemaining() {
      const total = parseFloat(this.editForm.totalDays) || 0;
      const carried = parseFloat(this.editForm.carriedOverDays) || 0;
      const used = parseFloat(this.editForm.usedDays) || 0;
      return (total + carried - used).toFixed(1);
    },
    isFullyInitialized() {
      return this.employeeBalances.every(b => b.status === 'Active');
    },
    toYearOptions() {
      return this.availableYears.filter(y => y > this.carryForwardForm.fromYear);
    },
    singleToYearOptions() {
      return this.availableYears.filter(y => y > this.singleCarryForm.fromYear);
    },
    hasCarriedOverDays() {
      return this.employeeBalances.some(b => parseFloat(b.carriedOverDays) > 0);
    },
    calculatedTotalAmount() {
      return (parseFloat(this.cashOutForm.days) || 0) * (parseFloat(this.cashOutForm.amountPerDay) || 0);
    }
  },
  mounted() {
    this.initializeData();
  },
  methods: {
    async initializeData() {
      await Promise.all([
        this.fetchYears(),
        this.fetchDepartments(),
        this.fetchLeaveTypes()
      ]);
      await this.fetchEmployees();
    },

    // Fetch available years
    async fetchYears() {
      const data = new FormData();
      data.append("function", "getAvailableYears");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.availableYears = res.data.data.years;
        }
      } catch (error) {
        console.error('Error fetching years:', error);
      }
    },

    // Fetch departments
    async fetchDepartments() {
      const data = new FormData();
      data.append("function", "getDepartmentsForFilter");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.departments = res.data.data.departments;
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },

    // Fetch leave types
    async fetchLeaveTypes() {
      const data = new FormData();
      data.append("function", "getActiveLeaveTypes");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.setLeaveTypes(res.data.data.leaveTypes);
        }
      } catch (error) {
        console.error('Error fetching leave types:', error);
      }
    },

    // Fetch employees with leave balance status
    async fetchEmployees() {
      this.loading = true;
      // Do NOT clear selectedEmployees here — preserve cross-page selections

      const data = new FormData();
      data.append("function", "getLeaveBalancesManagement");
      data.append("page", this.currentPage);
      data.append("limit", this.pageSize);
      data.append("year", this.selectedYear);
      data.append("search", this.searchValue);
      data.append("departmentId", this.selectedDepartment);
      data.append("initStatus", this.initStatusFilter);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const payload = res.data.data || {};
          if (Array.isArray(payload.leaveTypes) && payload.leaveTypes.length) {
            this.setLeaveTypes(payload.leaveTypes);
          }
          this.employees = (payload.employees || []).map(emp => ({
            ...emp,
            nationalId: this.normalizeNationalId(emp.nationalId),
            processing: false
          }));
          this.metrics = payload.metrics || this.metrics;
          this.totalItems = payload.totalCount || 0;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        this.showError('Failed to load employee data');
      } finally {
        this.loading = false;
      }
    },

    // View employee details
    async viewEmployeeDetails(emp) {
      this.selectedEmployee = emp;
      this.detailsLoading = true;
      $('#employee-details-modal').modal('show');

      const data = new FormData();
      data.append("function", "getEmployeeLeaveBalanceDetails");
      data.append("nationalId", emp.nationalId);
      data.append("year", this.selectedYear);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.employeeBalances = res.data.data.leaveBalances;
          this.leaveHistory = res.data.data.leaveHistory;
        }
      } catch (error) {
        console.error('Error fetching employee details:', error);
        this.showError('Failed to load employee details');
      } finally {
        this.detailsLoading = false;
      }
    },

    // Initialize single employee
    async initializeSingle(emp) {
      emp.processing = true;

      const data = new FormData();
      data.append("function", "initializeEmployeeLeaveBalances");
      data.append("nationalId", emp.nationalId);
      data.append("year", this.selectedYear);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        this.showError('Failed to initialize leave balances');
      } finally {
        emp.processing = false;
      }
    },

    // Initialize from details modal
    async initializeFromModal() {
      await this.initializeSingle(this.selectedEmployee);
      await this.viewEmployeeDetails(this.selectedEmployee);
    },

    // Edit balance
    editBalance(balance) {
      this.editForm = {
        leaveTypeId: balance.leaveTypeId,
        leaveTypeName: balance.typeName,
        totalDays: balance.totalDays || balance.defaultAllocation || 0,
        usedDays: balance.usedDays || 0,
        carriedOverDays: balance.carriedOverDays || 0,
        remarks: ''
      };
      $('#edit-balance-modal').modal('show');
    },

    // Save balance
    async saveBalance() {
      if (!this.editForm.totalDays && this.editForm.totalDays !== 0) {
        this.showError('Please enter allocated days');
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", "updateLeaveBalance");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("leaveTypeId", this.editForm.leaveTypeId);
      data.append("year", this.selectedYear);
      data.append("totalDays", this.editForm.totalDays);
      data.append("usedDays", this.editForm.usedDays);
      data.append("carriedOverDays", this.editForm.carriedOverDays);
      data.append("remarks", this.editForm.remarks);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#edit-balance-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.viewEmployeeDetails(this.selectedEmployee);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error saving balance:', error);
        this.showError('Failed to save leave balance');
      } finally {
        this.saving = false;
      }
    },

    // Open quick adjust modal
    openAdjustModal(emp) {
      this.adjustForm = {
        nationalId: emp.nationalId,
        employeeName: emp.fullName,
        leaveTypeId: '',
        adjustmentType: 'deduct',
        days: 1,
        reason: ''
      };
      $('#quick-adjust-modal').modal('show');
    },

    // Submit quick adjustment
    async submitAdjustment() {
      if (!this.adjustForm.leaveTypeId || !this.adjustForm.days || !this.adjustForm.reason) {
        this.showError('Please fill all required fields');
        return;
      }

      this.adjusting = true;

      const data = new FormData();
      data.append("function", "adjustLeaveBalance");
      data.append("nationalId", this.adjustForm.nationalId);
      data.append("leaveTypeId", this.adjustForm.leaveTypeId);
      data.append("year", this.selectedYear);
      data.append("adjustmentType", this.adjustForm.adjustmentType);
      data.append("days", this.adjustForm.days);
      data.append("reason", this.adjustForm.reason);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#quick-adjust-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error adjusting balance:', error);
        this.showError('Failed to adjust leave balance');
      } finally {
        this.adjusting = false;
      }
    },

    normalizeNationalId(id) {
      if (id === null || id === undefined) return '';
      return String(id).trim();
    },

    isEmployeeSelected(nationalId) {
      const id = this.normalizeNationalId(nationalId);
      return this.selectedEmployees.some(selectedId => this.normalizeNationalId(selectedId) === id);
    },

    toggleEmployeeSelection(nationalId, event) {
      const id = this.normalizeNationalId(nationalId);
      if (!id) return;

      if (event.target.checked) {
        if (!this.isEmployeeSelected(id)) {
          this.selectedEmployees = [...this.selectedEmployees, id];
        }
      } else {
        this.selectedEmployees = this.selectedEmployees.filter(
          selectedId => this.normalizeNationalId(selectedId) !== id
        );
        this.showSelectAllBanner = false;
      }
    },

    onFiltersChange() {
      this.currentPage = 1;
      this.clearSelection();
      this.fetchEmployees();
    },

    // Toggle current page selection
    toggleSelectPage() {
      const pageIds = this.employees.map(e => this.normalizeNationalId(e.nationalId)).filter(Boolean);
      const allPageSelected = pageIds.length > 0 && pageIds.every(id => this.isEmployeeSelected(id));
      if (allPageSelected) {
        this.selectedEmployees = this.selectedEmployees.filter(
          id => !pageIds.includes(this.normalizeNationalId(id))
        );
        this.showSelectAllBanner = false;
      } else {
        const newIds = pageIds.filter(id => !this.isEmployeeSelected(id));
        this.selectedEmployees = [...this.selectedEmployees, ...newIds];
        this.showSelectAllBanner = this.totalItems > this.employees.length;
      }
    },

    // Select ALL employees across all pages by fetching all IDs
    async selectAllEmployees() {
      this.selectingAll = true;
      try {
        const data = new FormData();
        data.append('function', 'getAllEmployeeNationalIds');
        data.append('year', this.selectedYear);
        data.append('search', this.searchValue);
        data.append('departmentId', this.selectedDepartment);
        data.append('initStatus', this.initStatusFilter);

        const res = await execute(data, authUrl);
        if (res.data.success) {
          const ids = (res.data.data?.nationalIds || []).map(id => this.normalizeNationalId(id)).filter(Boolean);
          this.selectedEmployees = ids;
          this.showSelectAllBanner = false;
          this.showSuccess(`All ${this.selectedEmployees.length} employees selected.`);
        } else {
          this.showError(res.data.message || 'Failed to select all employees');
        }
      } catch (e) {
        console.error('selectAllEmployees error:', e);
        this.showError('Failed to select all employees');
      } finally {
        this.selectingAll = false;
      }
    },

    // Clear all selections
    clearSelection() {
      this.selectedEmployees = [];
      this.showSelectAllBanner = false;
    },

    // Show bulk initialize modal
    showBulkInitializeModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }

      $('#bulk-init-modal').modal('show');
    },

    // Execute bulk initialize
    async executeBulkInitialize() {
      this.bulkInitializing = true;

      const data = new FormData();
      data.append("function", "bulkInitializeLeaveBalances");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("year", this.selectedYear);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#bulk-init-modal').modal('hide');
          this.showSuccess(res.data.message);
          this.clearSelection();
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error bulk initializing:', error);
        this.showError('Failed to bulk initialize');
      } finally {
        this.bulkInitializing = false;
      }
    },

    showBulkUninitializeModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.uninitializeForce = false;
      $('#bulk-uninit-modal').modal('show');
    },

    async executeBulkUninitialize() {
      this.bulkUninitializing = true;

      const data = new FormData();
      data.append('function', 'bulkUninitializeLeaveBalances');
      data.append('nationalIds', JSON.stringify(this.selectedEmployees));
      data.append('year', this.selectedYear);
      if (this.uninitializeForce) {
        data.append('force', '1');
      }

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#bulk-uninit-modal').modal('hide');
          this.showSuccess(res.data.message);
          this.clearSelection();
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error bulk uninitializing:', error);
        this.showError('Failed to bulk uninitialize leave balances');
      } finally {
        this.bulkUninitializing = false;
      }
    },

    showSingleUninitializeModal() {
      this.uninitializeForce = false;
      $('#single-uninit-modal').modal('show');
    },

    async executeSingleUninitialize() {
      if (!this.selectedEmployee?.nationalId) {
        this.showError('No employee selected');
        return;
      }

      this.uninitializingSingle = true;
      const data = new FormData();
      data.append('function', 'uninitializeEmployeeLeaveBalances');
      data.append('nationalId', this.normalizeNationalId(this.selectedEmployee.nationalId));
      data.append('year', this.selectedYear);
      if (this.uninitializeForce) {
        data.append('force', '1');
      }

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#single-uninit-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.viewEmployeeDetails(this.selectedEmployee);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error uninitializing employee balances:', error);
        this.showError('Failed to reset leave balances');
      } finally {
        this.uninitializingSingle = false;
      }
    },

    // Download report with progress
    async downloadReport() {
      // Reset progress
      this.downloadProgress = {
        status: 'Preparing...',
        message: 'Calculating total records...',
        percent: 0,
        fetched: 0,
        totalRecords: 0,
        timeRemaining: '--:--',
        cancelled: false,
        startTime: Date.now()
      };
      this.allDownloadData = [];

      // Show progress modal
      $('#download-progress-modal').modal('show');

      try {
        // First, get total count
        const countData = new FormData();
        countData.append("function", "getLeaveBalancesManagement");
        countData.append("page", 1);
        countData.append("limit", 1);
        countData.append("year", this.selectedYear);
        countData.append("search", this.searchValue);
        countData.append("departmentId", this.selectedDepartment);
        countData.append("initStatus", this.initStatusFilter);

        const countRes = await execute(countData, authUrl);
        if (!countRes.data.success) {
          throw new Error('Failed to get record count');
        }

        const totalRecords = countRes.data.data.totalCount;
        this.downloadProgress.totalRecords = totalRecords;
        this.downloadProgress.message = `Found ${totalRecords} records to download`;

        if (totalRecords === 0) {
          this.downloadProgress.status = 'No Data';
          this.downloadProgress.message = 'No records found to download';
          this.downloadProgress.percent = 100;
          return;
        }

        // Calculate pages needed (fetch 50 records per page for efficiency)
        const pageSize = 50;
        const totalPages = Math.ceil(totalRecords / pageSize);
        let currentPage = 1;
        let fetchedCount = 0;

        this.downloadProgress.status = 'Downloading...';

        // Fetch all pages
        while (currentPage <= totalPages && !this.downloadProgress.cancelled) {
          const pageData = new FormData();
          pageData.append("function", "getLeaveBalancesManagement");
          pageData.append("page", currentPage);
          pageData.append("limit", pageSize);
          pageData.append("year", this.selectedYear);
          pageData.append("search", this.searchValue);
          pageData.append("departmentId", this.selectedDepartment);
          pageData.append("initStatus", this.initStatusFilter);

          const pageRes = await execute(pageData, authUrl);

          if (pageRes.data.success && pageRes.data.data.employees) {
            this.allDownloadData.push(...pageRes.data.data.employees);
            fetchedCount += pageRes.data.data.employees.length;
          }

          // Update progress
          this.downloadProgress.fetched = fetchedCount;
          this.downloadProgress.percent = Math.round((currentPage / totalPages) * 100);
          this.downloadProgress.message = `Fetching page ${currentPage} of ${totalPages}...`;

          // Calculate time remaining
          const elapsed = Date.now() - this.downloadProgress.startTime;
          const avgTimePerPage = elapsed / currentPage;
          const remainingPages = totalPages - currentPage;
          const remainingMs = avgTimePerPage * remainingPages;
          this.downloadProgress.timeRemaining = this.formatTimeRemaining(remainingMs);

          currentPage++;

          // Small delay to prevent overwhelming the server
          await this.sleep(100);
        }

        if (this.downloadProgress.cancelled) {
          this.downloadProgress.status = 'Cancelled';
          this.downloadProgress.message = 'Download was cancelled';
          return;
        }

        // Generate CSV
        this.downloadProgress.status = 'Generating CSV...';
        this.downloadProgress.message = 'Creating downloadable file...';
        this.downloadProgress.percent = 95;

        await this.sleep(300);

        this.generateCSV();

        // Complete
        this.downloadProgress.status = 'Complete!';
        this.downloadProgress.message = `Successfully downloaded ${fetchedCount} records`;
        this.downloadProgress.percent = 100;
        this.downloadProgress.timeRemaining = '00:00';

      } catch (error) {
        console.error('Download error:', error);
        this.downloadProgress.status = 'Error';
        this.downloadProgress.message = error.message || 'Failed to download report';
        this.downloadProgress.percent = 0;
      }
    },

    // Generate CSV from downloaded data
    generateCSV() {
      if (this.allDownloadData.length === 0) return;

      // Define CSV headers
      const headers = [
        'Employee No',
        'National ID',
        'Employee Name',
        'Department',
        'Position',
        'Total Leave Days',
        'Used Days',
        'Remaining Days',
        'Consumption %',
        'Status'
      ];

      // Map data to CSV rows
      const rows = this.allDownloadData.map(emp => {
        const total = parseFloat(emp.totalLeaveDays) || 0;
        const used = parseFloat(emp.usedLeaveDays) || 0;
        const remaining = parseFloat(emp.remainingLeaveDays) || 0;
        const consumption = total > 0 ? Math.round((used / total) * 100) : 0;

        return [
          emp.empNo || '',
          emp.nationalId || '',
          (emp.fullName || '').replace(/,/g, ' ').trim(),
          (emp.departmentName || '').replace(/,/g, ' '),
          (emp.positionTitle || '-').replace(/,/g, ' '),
          total.toFixed(1),
          used.toFixed(1),
          remaining.toFixed(1),
          consumption + '%',
          emp.initializationStatus || ''
        ];
      });

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      rows.forEach(row => {
        csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
      });

      // Create and download file
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `LeaveBalances_${this.selectedYear}_${this.formatDateForFilename()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },

    // Cancel download
    cancelDownload() {
      this.downloadProgress.cancelled = true;
    },

    // Format time remaining
    formatTimeRemaining(ms) {
      if (ms <= 0 || isNaN(ms)) return '00:00';

      const seconds = Math.ceil(ms / 1000);
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;

      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    // Format date for filename
    formatDateForFilename() {
      const now = new Date();
      return `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2,'0')}${now.getDate().toString().padStart(2,'0')}_${now.getHours().toString().padStart(2,'0')}${now.getMinutes().toString().padStart(2,'0')}`;
    },

    // Sleep helper
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Toggle select all
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedEmployees = [];
      } else {
        this.selectedEmployees = this.employees.map(emp => emp.nationalId);
      }
    },

    // Pagination methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchEmployees();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchEmployees();
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.fetchEmployees();
      }
    },

    // Utility methods
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getInitials(name) {
      if (!name) return 'NA';
      return name.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    setLeaveTypes(types) {
      this.leaveTypes = this.enrichLeaveTypeHeaders(types);
    },

    enrichLeaveTypeHeaders(types) {
      return (types || []).map(lt => {
        const lower = String(lt.typeName || '').trim().toLowerCase();
        let headerAcronym = null;
        let headerLabel = null;
        if (this.leaveTypeNameIsMaternity(lower)) {
          headerAcronym = 'ML';
          headerLabel = 'Maternity Leave';
        } else if (this.leaveTypeNameIsPaternity(lower)) {
          headerAcronym = 'PL';
          headerLabel = 'Paternity Leave';
        }
        return { ...lt, headerAcronym, headerLabel };
      });
    },

    shortLeaveTypeName(name) {
      const n = String(name || '').trim();
      const lower = n.toLowerCase();
      if (this.leaveTypeNameIsMaternity(n)) return 'ML (Maternity Leave)';
      if (this.leaveTypeNameIsPaternity(n)) return 'PL (Paternity Leave)';
      if (n.length <= 14) return n;
      const words = n.split(/\s+/);
      if (words.length > 1) {
        return words.map(w => w[0]).join('').toUpperCase().slice(0, 6);
      }
      return n.slice(0, 12) + '…';
    },

    getBalanceCell(emp, leaveTypeId) {
      const list = emp.balancesByType || [];
      const hit = list.find(b => String(b.leaveTypeId) === String(leaveTypeId));
      return hit || { initialized: false, applies: true, remainingDays: 0, totalDays: 0, usedDays: 0 };
    },

    normalizeEmployeeGender(raw) {
      const gl = String(raw || '').toLowerCase().trim();
      if (raw === '1' || gl === 'm' || gl === 'male' || gl === 'man' || gl === 'masculine') return 'Male';
      if (raw === '2' || gl === 'f' || gl === 'female' || gl === 'woman' || gl === 'feminine') return 'Female';
      return '';
    },

    leaveTypeNameIsMaternity(name) {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return false;
      if (/maternity|maternal/.test(n)) return true;
      return /^(ml)(\s|$|[^a-z])/i.test(n);
    },

    leaveTypeNameIsPaternity(name) {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return false;
      if (/paternity|paternal/.test(n)) return true;
      return /^(pl)(\s|$|[^a-z])/i.test(n);
    },

    leaveTypeApplies(emp, lt) {
      const cell = this.getBalanceCell(emp, lt.id);
      if (cell && typeof cell.applies === 'boolean') return cell.applies;
      const name = lt.typeName || '';
      const g = this.normalizeEmployeeGender(emp.gender);
      if (this.leaveTypeNameIsMaternity(name)) return g === 'Female';
      if (this.leaveTypeNameIsPaternity(name)) return g === 'Male';
      return true;
    },

    balanceTotalDisplay(cell) {
      const t = parseFloat(cell.totalDays) || 0;
      const c = parseFloat(cell.carriedOverDays) || 0;
      return t + c;
    },

    balanceTooltip(cell) {
      const rem = parseFloat(cell.remainingDays) || 0;
      const used = parseFloat(cell.usedDays) || 0;
      const total = this.balanceTotalDisplay(cell);
      return `Remaining: ${rem} | Used: ${used} | Total: ${total}`;
    },

    getStatusClass(status) {
      const classes = {
        'Initialized': 'hr-badge-success',
        'Partially Initialized': 'hr-badge-warning',
        'Not Initialized': 'hr-badge-danger'
      };
      return classes[status] || 'hr-badge-muted';
    },

    // Get consumption percentage
    getConsumptionPercent(emp) {
      const total = parseFloat(emp.totalLeaveDays) || 0;
      const used = parseFloat(emp.usedLeaveDays) || 0;
      if (total === 0) return 0;
      return Math.min(Math.round((used / total) * 100), 100);
    },

    // Get progress bar color class based on consumption
    getProgressBarClass(emp) {
      const percent = this.getConsumptionPercent(emp);
      if (percent >= 90) return 'bg-danger';
      if (percent >= 75) return 'bg-warning';
      if (percent >= 50) return 'bg-info';
      return 'bg-success';
    },

    getRemainingDaysClass(emp) {
      const remaining = parseFloat(emp.remainingLeaveDays) || 0;
      const total = parseFloat(emp.totalLeaveDays) || 0;
      if (total === 0) return 'remaining-muted';
      const percent = (remaining / total) * 100;
      if (percent <= 10) return 'remaining-danger';
      if (percent <= 25) return 'remaining-warning';
      return 'remaining-success';
    },

    getLeaveStatusClass(status) {
      const classes = {
        'Approved': 'bg-success',
        'Pending': 'bg-warning',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getRemainingClass(balance) {
      const remaining = parseFloat(balance.remainingDays) || 0;
      const total = parseFloat(balance.totalDays) || 0;

      if (balance.status !== 'Active') return 'bg-secondary';
      if (remaining <= 0) return 'bg-danger';
      if (remaining < total * 0.25) return 'bg-warning';
      return 'bg-success';
    },

    showSuccess(message) {
      this.successMessage = message;
      this.$nextTick(() => this.$refs.successModal?.open());
    },

    showError(message) {
      this.errorMessage = message;
      this.$nextTick(() => this.$refs.errorModal?.open());
    },

    // =====================
    // CARRY FORWARD METHODS
    // =====================

    // Show bulk carry forward modal
    showCarryForwardModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.carryForwardForm.fromYear = this.selectedYear - 1;
      this.carryForwardForm.toYear = this.selectedYear;
      this.carryForwardPreview = [];
      this.totalEligibleDays = 0;

      // Load preview if single employee selected
      if (this.selectedEmployees.length === 1) {
        this.loadCarryForwardPreview();
      }

      $('#carry-forward-modal').modal('show');
    },

    // Load carry forward preview for bulk (single employee)
    async loadCarryForwardPreview() {
      if (this.selectedEmployees.length !== 1) return;

      this.previewLoading = true;
      const data = new FormData();
      data.append("function", "getCarryForwardPreview");
      data.append("nationalId", this.selectedEmployees[0]);
      data.append("fromYear", this.carryForwardForm.fromYear);
      data.append("toYear", this.carryForwardForm.toYear);
      data.append("maxCarryForward", this.carryForwardForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.carryForwardPreview = res.data.data.preview;
          this.totalEligibleDays = res.data.data.totalEligibleDays;
        }
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        this.previewLoading = false;
      }
    },

    // Execute bulk carry forward
    async executeCarryForward() {
      if (this.carryForwardForm.fromYear >= this.carryForwardForm.toYear) {
        this.showError('Target year must be greater than source year');
        return;
      }

      this.carryingForward = true;

      const data = new FormData();
      data.append("function", "bulkCarryForwardLeaveBalances");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("fromYear", this.carryForwardForm.fromYear);
      data.append("toYear", this.carryForwardForm.toYear);
      data.append("maxCarryForward", this.carryForwardForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#carry-forward-modal').modal('hide');
          this.showSuccess(res.data.message);
          this.selectedEmployees = [];
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error carrying forward:', error);
        this.showError('Failed to carry forward leave balances');
      } finally {
        this.carryingForward = false;
      }
    },

    // Open single employee carry forward modal
    openSingleCarryForward() {
      this.singleCarryForm.fromYear = this.selectedYear - 1;
      this.singleCarryForm.toYear = this.selectedYear;
      this.singleCarryPreview = [];
      this.singleTotalEligible = 0;

      $('#employee-details-modal').modal('hide');
      setTimeout(() => {
        this.loadSingleCarryPreview();
        $('#single-carry-forward-modal').modal('show');
      }, 300);
    },

    // Load single employee carry forward preview
    async loadSingleCarryPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.singlePreviewLoading = true;
      const data = new FormData();
      data.append("function", "getCarryForwardPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("fromYear", this.singleCarryForm.fromYear);
      data.append("toYear", this.singleCarryForm.toYear);
      data.append("maxCarryForward", this.singleCarryForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.singleCarryPreview = res.data.data.preview;
          this.singleTotalEligible = res.data.data.totalEligibleDays;
        }
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        this.singlePreviewLoading = false;
      }
    },

    // Execute single employee carry forward
    async executeSingleCarryForward() {
      if (this.singleCarryForm.fromYear >= this.singleCarryForm.toYear) {
        this.showError('Target year must be greater than source year');
        return;
      }

      this.singleCarryingForward = true;

      const data = new FormData();
      data.append("function", "carryForwardLeaveBalances");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("fromYear", this.singleCarryForm.fromYear);
      data.append("toYear", this.singleCarryForm.toYear);
      data.append("maxCarryForward", this.singleCarryForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#single-carry-forward-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error carrying forward:', error);
        this.showError('Failed to carry forward leave balances');
      } finally {
        this.singleCarryingForward = false;
      }
    },

    // =====================
    // ROLLBACK METHODS
    // =====================

    // Open rollback modal
    openRollbackModal() {
      this.rollbackForm.year = this.selectedYear;
      this.rollbackForm.reason = '';
      this.rollbackPreview = [];
      this.totalCarriedOverToRollback = 0;

      $('#employee-details-modal').modal('hide');
      setTimeout(() => {
        this.loadRollbackPreview();
        $('#rollback-modal').modal('show');
      }, 300);
    },

    // Load rollback preview
    async loadRollbackPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.rollbackPreviewLoading = true;
      const data = new FormData();
      data.append("function", "getRollbackPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.rollbackForm.year);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.rollbackPreview = res.data.data.preview;
          this.totalCarriedOverToRollback = res.data.data.totalCarriedOver;
        }
      } catch (error) {
        console.error('Error loading rollback preview:', error);
      } finally {
        this.rollbackPreviewLoading = false;
      }
    },

    // Execute rollback
    async executeRollback() {
      if (!this.rollbackForm.reason) {
        this.showError('Please provide a reason for the rollback');
        return;
      }

      this.rollingBack = true;

      const data = new FormData();
      data.append("function", "rollbackCarryForward");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.rollbackForm.year);
      data.append("reason", this.rollbackForm.reason);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#rollback-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error executing rollback:', error);
        this.showError('Failed to rollback carry forward');
      } finally {
        this.rollingBack = false;
      }
    },

    // =====================
    // CASH OUT METHODS
    // =====================

    // Show bulk cash out modal
    showCashOutModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.bulkCashOutForm = {
        year: this.selectedYear,
        leaveTypeId: '',
        maxDays: '',
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      };
      $('#bulk-cashout-modal').modal('show');
    },

    // Open single employee cash out modal
    openSingleCashOutModal() {
      this.cashOutForm = {
        year: this.selectedYear,
        leaveTypeId: '',
        days: 0,
        maxDays: 0,
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      };
      this.cashOutBalances = [];
      this.suggestedDailyRate = 0;

      $('#employee-details-modal').modal('hide');
      setTimeout(() => {
        this.loadCashOutPreview();
        $('#single-cashout-modal').modal('show');
      }, 300);
    },

    // Load cash out preview
    async loadCashOutPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.cashOutPreviewLoading = true;
      const data = new FormData();
      data.append("function", "getCashOutPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.cashOutForm.year);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.cashOutBalances = res.data.data.balances;
          this.suggestedDailyRate = res.data.data.suggestedDailyRate || 0;
          if (this.suggestedDailyRate > 0) {
            this.cashOutForm.amountPerDay = this.suggestedDailyRate;
          }
        }
      } catch (error) {
        console.error('Error loading cash out preview:', error);
      } finally {
        this.cashOutPreviewLoading = false;
      }
    },

    // Handle leave type change in cash out form
    onLeaveTypeChange() {
      const selected = this.cashOutBalances.find(b => b.leaveTypeID === this.cashOutForm.leaveTypeId);
      if (selected) {
        this.cashOutForm.maxDays = parseFloat(selected.remainingDays);
        this.cashOutForm.days = parseFloat(selected.remainingDays);
      } else {
        this.cashOutForm.maxDays = 0;
        this.cashOutForm.days = 0;
      }
    },

    // Execute single cash out
    async executeCashOut() {
      if (!this.cashOutForm.leaveTypeId || this.cashOutForm.days <= 0) {
        this.showError('Please select a leave type and enter days to cash out');
        return;
      }

      if (this.cashOutForm.days > this.cashOutForm.maxDays) {
        this.showError(`Cannot cash out more than ${this.cashOutForm.maxDays} days`);
        return;
      }

      this.cashingOut = true;

      const data = new FormData();
      data.append("function", "cashOutLeave");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.cashOutForm.year);
      data.append("leaveTypeId", this.cashOutForm.leaveTypeId);
      data.append("days", this.cashOutForm.days);
      data.append("amountPerDay", this.cashOutForm.amountPerDay);
      data.append("paymentReference", this.cashOutForm.paymentReference);
      data.append("remarks", this.cashOutForm.remarks);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#single-cashout-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error cashing out:', error);
        this.showError('Failed to process cash out');
      } finally {
        this.cashingOut = false;
      }
    },

    // Execute bulk cash out
    async executeBulkCashOut() {
      if (!this.bulkCashOutForm.leaveTypeId) {
        this.showError('Please select a leave type');
        return;
      }

      this.bulkCashingOut = true;

      const data = new FormData();
      data.append("function", "bulkCashOutLeave");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("year", this.bulkCashOutForm.year);
      data.append("leaveTypeId", this.bulkCashOutForm.leaveTypeId);
      data.append("maxDays", this.bulkCashOutForm.maxDays || '');
      data.append("amountPerDay", this.bulkCashOutForm.amountPerDay);
      data.append("paymentReference", this.bulkCashOutForm.paymentReference);
      data.append("remarks", this.bulkCashOutForm.remarks);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#bulk-cashout-modal').modal('hide');
          this.showSuccess(res.data.message);
          this.selectedEmployees = [];
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error bulk cashing out:', error);
        this.showError('Failed to process bulk cash out');
      } finally {
        this.bulkCashingOut = false;
      }
    },

    // Format currency
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(amount || 0);
    }
  }
};
</script>

<style scoped>
.avatar-initials {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
  background: #dbeafe;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

.emp-name { font-weight: 600; font-size: 14px; }
.emp-meta { font-size: 12px; color: #64748b; display: block; }
.text-primary-bold { font-weight: 700; color: #3b82f6; }
.text-warning-bold { font-weight: 700; color: #f59e0b; }
.remaining-muted { color: #94a3b8; font-weight: 600; }
.remaining-danger { color: #ef4444; font-weight: 600; }
.remaining-warning { color: #f59e0b; font-weight: 600; }
.remaining-success { color: #10b981; font-weight: 600; }
.consumption-wrapper { min-width: 120px; }
.hr-checkbox { width: 18px; height: 18px; accent-color: #059669; cursor: pointer; }

.selection-bar,
.select-all-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.select-all-banner {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.select-all-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.success-icon, .error-icon { font-size: 48px; margin-bottom: 12px; }
.success-icon { color: #10b981; }
.error-icon { color: #ef4444; }
.success-heading { color: #10b981; margin: 0 0 8px 0; }
.error-heading { color: #ef4444; margin: 0 0 8px 0; }

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
}

.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
}

.bg-soft-info {
  background-color: rgba(52, 195, 143, 0.1);
}

.bg-soft-secondary {
  background-color: rgba(116, 120, 141, 0.1);
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.table th {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  white-space: nowrap;
}

.form-check-input {
  cursor: pointer;
}

.modal-xl {
  max-width: 1140px;
}

/* Progress bar styles */
.consumption-wrapper {
  min-width: 180px;
}

.progress {
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
  border-radius: 4px;
}

.progress-bar.bg-success {
  background: linear-gradient(90deg, #34c38f 0%, #2ecc71 100%) !important;
}

.progress-bar.bg-info {
  background: linear-gradient(90deg, #50a5f1 0%, #3498db 100%) !important;
}

.progress-bar.bg-warning {
  background: linear-gradient(90deg, #f1b44c 0%, #f39c12 100%) !important;
}

.progress-bar.bg-danger {
  background: linear-gradient(90deg, #f46a6a 0%, #e74c3c 100%) !important;
}

/* Table row hover effect */
.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.05);
}

/* Badge styles */
.badge.bg-soft-secondary {
  color: #74788d;
}

/* Stats cards animation */
.mini-stats-wid {
  transition: transform 0.2s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
}

/* Download progress modal */
.download-icon-wrapper {
  display: inline-block;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

#download-progress-modal .progress {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

#download-progress-modal .progress-bar {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 20px;
}

#download-progress-modal .modal-body h5 {
  color: #495057;
}

#download-progress-modal .border-end {
  border-color: #e9ecef !important;
}

/* Ensure all modals and their contents are fully clickable */
.modal {
  pointer-events: auto !important;
}

.modal-dialog {
  pointer-events: auto !important;
}

.modal-content {
  pointer-events: auto !important;
}

.modal-footer {
  pointer-events: auto !important;
}

.modal-footer button,
.modal-footer .btn {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* Remove any backdrop blocking */
.modal-backdrop {
  pointer-events: none !important;
}

/* Ensure buttons in all modals are interactive */
.btn {
  pointer-events: auto !important;
}

.leave-type-col {
  min-width: 72px;
  vertical-align: bottom;
}

.leave-type-hdr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.2;
}

.leave-type-acronym {
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.leave-type-desc {
  display: block;
  font-size: 0.65rem;
  color: #6c757d;
  font-weight: 500;
  text-align: center;
  max-width: 88px;
  line-height: 1.15;
}
</style>